'use client';

/**
 * 通知铃铛组件 - Vercel风格
 * 显示未读通知数量和下拉通知列表
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bell,
  Check,
  CheckCheck,
  Clock,
  Mail,
  MessageSquare,
  AlertTriangle,
  User,
  ChevronRight,
  X,
  Sparkles,
} from 'lucide-react';
import Link from 'next/link';

// =====================================================
// Types
// =====================================================

type NotificationType =
  | 'inquiry_new'
  | 'inquiry_assigned'
  | 'inquiry_followup'
  | 'message_unreplied'
  | 'contact_inactive'
  | 'deal_stalled'
  | 'system_alert'
  | 'user_mention';

type NotificationCategory = 'work' | 'system';
type NotificationSeverity = 'critical' | 'warning' | 'info';

interface Notification {
  id: number;
  type: NotificationType;
  category: NotificationCategory;
  severity: NotificationSeverity;
  title: string;
  content: string | null;
  link_url: string | null;
  read_at: string | null;
  created_at: string;
}

// =====================================================
// Config
// =====================================================

const typeConfig: Record<NotificationType, { icon: typeof Bell; label: string }> = {
  inquiry_new: { icon: Mail, label: '新询盘' },
  inquiry_assigned: { icon: User, label: '询盘分配' },
  inquiry_followup: { icon: Clock, label: '待跟进' },
  message_unreplied: { icon: MessageSquare, label: '待回复' },
  contact_inactive: { icon: User, label: '客户提醒' },
  deal_stalled: { icon: AlertTriangle, label: '商机滞留' },
  system_alert: { icon: AlertTriangle, label: '系统告警' },
  user_mention: { icon: User, label: '@提及' },
};

const severityConfig: Record<NotificationSeverity, { dot: string; bg: string }> = {
  critical: { dot: 'bg-red-500', bg: 'bg-red-50' },
  warning: { dot: 'bg-amber-500', bg: 'bg-amber-50' },
  info: { dot: 'bg-blue-500', bg: 'bg-blue-50' },
};

// =====================================================
// Helpers
// =====================================================

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return '刚刚';
  if (diffMins < 60) return `${diffMins}分钟前`;
  if (diffHours < 24) return `${diffHours}小时前`;
  if (diffDays < 7) return `${diffDays}天前`;
  return date.toLocaleDateString('zh-CN');
}

/**
 * 请求桌面通知权限
 */
async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
}

/**
 * 发送桌面通知（无声音）
 */
function sendDesktopNotification(title: string, body: string, onClick?: () => void) {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  const notification = new Notification(title, {
    body,
    silent: true, // 无声音
    tag: 'admin-notification', // 相同 tag 会覆盖旧通知
  });

  if (onClick) {
    notification.onclick = () => {
      window.focus();
      onClick();
      notification.close();
    };
  }

  // 5秒后自动关闭
  setTimeout(() => notification.close(), 5000);
}

// =====================================================
// Hook: useNotifications
// =====================================================

function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadByCategory, setUnreadByCategory] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const hasDesktopPermissionRef = useRef(false);

  // 请求桌面通知权限
  useEffect(() => {
    requestNotificationPermission().then((granted) => {
      hasDesktopPermissionRef.current = granted;
      console.log('[通知] 桌面通知权限:', granted ? '已授权' : '未授权');
    });
  }, []);

  // 获取初始通知列表
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/notifications?limit=20', {
        credentials: 'include',
      });
      const data = await response.json();

      if (data.success) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
        setUnreadByCategory(data.unreadByCategory || {});
        setError(null);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('获取通知失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 连接 SSE 实时流
  useEffect(() => {
    // 先获取初始数据
    fetchNotifications();

    // 建立 SSE 连接
    const connectSSE = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      const eventSource = new EventSource('/api/admin/notifications/stream', {
        withCredentials: true,
      });

      eventSource.onopen = () => {
        console.log('[SSE] 连接已建立');
        setIsConnected(true);
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'connected') {
            console.log('[SSE] 服务器确认连接');
          } else if (data.type === 'new_notification') {
            console.log('[SSE] 收到新通知:', data.notification.title);

            // 更新通知列表
            setNotifications((prev) => {
              // 避免重复
              if (prev.some((n) => n.id === data.notification.id)) {
                return prev;
              }
              return [data.notification, ...prev].slice(0, 20);
            });

            // 更新未读数
            if (data.unreadCount !== undefined) {
              setUnreadCount(data.unreadCount);
            } else {
              setUnreadCount((prev) => prev + 1);
            }

            // 发送桌面通知
            if (hasDesktopPermissionRef.current) {
              const config = typeConfig[data.notification.type as NotificationType] || typeConfig.system_alert;
              console.log('[通知] 发送桌面通知:', data.notification.title);
              sendDesktopNotification(
                `${config.label}: ${data.notification.title}`,
                data.notification.content || '',
                data.notification.link_url
                  ? () => (window.location.href = data.notification.link_url)
                  : undefined
              );
            } else {
              console.log('[通知] 桌面通知未授权，跳过');
            }
          }
        } catch (e) {
          // 忽略解析错误（如心跳）
        }
      };

      eventSource.onerror = (error) => {
        console.log('[SSE] 连接错误，5秒后重连');
        setIsConnected(false);
        eventSource.close();

        // 5秒后重连
        setTimeout(connectSSE, 5000);
      };

      eventSourceRef.current = eventSource;
    };

    connectSSE();

    // 清理
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (ids: number[]) => {
    try {
      const response = await fetch('/api/admin/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'read', ids }),
      });
      const data = await response.json();
      if (data.success) {
        // 更新本地状态
        setNotifications((prev) =>
          prev.map((n) =>
            ids.includes(n.id) ? { ...n, read_at: new Date().toISOString() } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - data.readCount));
      }
    } catch (err) {
      console.error('标记已读失败:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'read-all' }),
      });
      const data = await response.json();
      if (data.success) {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
        );
        setUnreadCount(0);
        setUnreadByCategory({});
      }
    } catch (err) {
      console.error('全部已读失败:', err);
    }
  }, []);

  return {
    notifications,
    unreadCount,
    unreadByCategory,
    loading,
    error,
    isConnected,
    refresh: fetchNotifications,
    markAsRead,
    markAllAsRead,
  };
}

// =====================================================
// Component: NotificationItem
// =====================================================

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: number) => void;
  onClose: () => void;
}

function NotificationItem({ notification, onRead, onClose }: NotificationItemProps) {
  const config = typeConfig[notification.type] || typeConfig.system_alert;
  const severity = severityConfig[notification.severity];
  const Icon = config.icon;
  const isUnread = !notification.read_at;

  const handleClick = () => {
    if (isUnread) {
      onRead(notification.id);
    }
    if (notification.link_url) {
      onClose();
    }
  };

  const content = (
    <div
      className={`px-3 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer ${
        isUnread ? 'bg-blue-50/30' : ''
      }`}
      onClick={handleClick}
    >
      <div className="flex items-start gap-2.5">
        {/* 未读指示点 */}
        <div className="pt-1.5">
          {isUnread ? (
            <div className={`w-2 h-2 rounded-full ${severity.dot}`} />
          ) : (
            <div className="w-2 h-2" />
          )}
        </div>

        {/* 图标 */}
        <div className={`p-1.5 rounded ${severity.bg}`}>
          <Icon className="w-3.5 h-3.5 text-gray-600" />
        </div>

        {/* 内容 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-xs text-gray-400">{config.label}</span>
            <span className="text-xs text-gray-300">·</span>
            <span className="text-xs text-gray-400">{formatTime(notification.created_at)}</span>
          </div>
          <p className={`text-sm truncate ${isUnread ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
            {notification.title}
          </p>
          {notification.content && (
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{notification.content}</p>
          )}
        </div>

        {/* 跳转箭头 */}
        {notification.link_url && (
          <ChevronRight className="w-4 h-4 text-gray-300 mt-1 shrink-0" />
        )}
      </div>
    </div>
  );

  if (notification.link_url) {
    return <Link href={notification.link_url}>{content}</Link>;
  }

  return content;
}

// =====================================================
// Component: NotificationBell
// =====================================================

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<'all' | NotificationCategory>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    unreadByCategory,
    loading,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  // 点击外部关闭下拉框
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // 过滤通知
  const filteredNotifications =
    activeCategory === 'all'
      ? notifications
      : notifications.filter((n) => n.category === activeCategory);

  const handleMarkAsRead = (id: number) => {
    markAsRead([id]);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 铃铛按钮 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 text-gray-500 relative transition-colors"
        title="通知"
      >
        <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center px-1">
            <span className="text-[10px] text-white font-medium">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </span>
        )}
      </button>

      {/* 下拉通知面板 */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
          {/* 头部 */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">通知</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                全部已读
              </button>
            )}
          </div>

          {/* 分类筛选 */}
          <div className="px-3 py-2 border-b border-gray-100 flex gap-1">
            {[
              { key: 'all', label: '全部' },
              { key: 'work', label: '工作' },
              { key: 'system', label: '系统' },
            ].map((tab) => {
              const count =
                tab.key === 'all'
                  ? unreadCount
                  : unreadByCategory[tab.key] || 0;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveCategory(tab.key as 'all' | NotificationCategory)}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors flex items-center gap-1.5 ${
                    activeCategory === tab.key
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                  {count > 0 && (
                    <span
                      className={`min-w-[16px] h-4 rounded-full px-1 text-[10px] flex items-center justify-center ${
                        activeCategory === tab.key
                          ? 'bg-white/20 text-white'
                          : 'bg-red-100 text-red-600'
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* 通知列表 */}
          <div className="max-h-[360px] overflow-y-auto">
            {loading ? (
              <div className="p-4 space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-start gap-3 animate-pulse">
                    <div className="w-8 h-8 bg-gray-100 rounded" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-gray-100 rounded w-3/4" />
                      <div className="h-3 bg-gray-100 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                <p className="text-sm text-gray-400">暂无通知</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {filteredNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRead={handleMarkAsRead}
                    onClose={() => setIsOpen(false)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* 底部 */}
          {filteredNotifications.length > 0 && (
            <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <Link
                href="/admin-2025/settings/changelog"
                onClick={() => setIsOpen(false)}
                className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
              >
                <Sparkles className="w-3 h-3" />
                更新日志
              </Link>
              <Link
                href="/admin-2025/notifications"
                onClick={() => setIsOpen(false)}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                查看全部
                <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
